import { z } from 'zod';

/**
 * Validação de ambiente. Falha ruidosamente na subida, não silenciosamente às 3h da manhã.
 *
 * Cada serviço valida só o que consome — o worker não precisa saber a origem do CORS,
 * e a api não precisa do intervalo de reconciliação.
 */

const nodeEnv = z.enum(['development', 'test', 'production']).default('development');

const base = z.object({
  NODE_ENV: nodeEnv,
  LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).default('info'),
});

/**
 * Remetente de e-mail: aceita `pessoa@dominio` e também `Nome <pessoa@dominio>`.
 *
 * Mora aqui, e não dentro do schema de cada serviço, porque api **e** worker enviam
 * e-mail. Quando a regra existia duplicada, corrigi só a da api e o worker passou a
 * reiniciar em laço em produção com `MGDLead <nao-responda@mgdleads.tech>`.
 *
 * Remetente com nome é o que o cliente de e-mail exibe e ajuda entregabilidade — por
 * isso a regra é essa, e não `z.string().email()`.
 */
export const mailFromSchema = z
  .string()
  .refine(
    (v) => {
      const m = /^\s*(?:.*<\s*([^>]+)\s*>|([^<>]+))\s*$/.exec(v);
      const endereco = (m?.[1] ?? m?.[2] ?? '').trim();
      return z.string().email().safeParse(endereco).success;
    },
    { message: 'MAIL_FROM deve ser um e-mail ou "Nome <email@dominio>"' },
  )
  .default('nao-responda@mgdlead.local');

const database = z.object({
  DATABASE_URL: z.string().url().startsWith('postgres'),
});

const redis = z.object({
  REDIS_URL: z.string().url().startsWith('redis'),
});

export const apiEnvSchema = base
  .merge(database)
  .merge(redis)
  .extend({
    PORT: z.coerce.number().int().positive().default(3001),
    HOST: z.string().default('0.0.0.0'),
    /** Segredo do Better Auth. 32+ bytes aleatórios. Sem default, de propósito. */
    BETTER_AUTH_SECRET: z
      .string()
      .min(32, 'BETTER_AUTH_SECRET precisa de pelo menos 32 caracteres'),
    BETTER_AUTH_URL: z.string().url(),
    /** Origem do app web, para CORS e cookies. */
    APP_URL: z.string().url(),
    /** Host público do redirecionador (pode ser o mesmo da API em dev). */
    REDIRECT_BASE_URL: z.string().url(),
    SMTP_URL: z.string().optional(),
    MAIL_FROM: mailFromSchema,
    /**
     * Segredo do webhook do Resend (`whsec_…`), do painel dele.
     *
     * Opcional: sem ele a rota `/webhooks/resend` **não é registrada**. É o que fecha o
     * ciclo do e-mail — sem webhook, `SENT` significa só "o provedor aceitou", nunca
     * "a pessoa recebeu". Uma rota sem segredo aceitaria qualquer corpo, e qualquer um
     * poderia marcar entregas alheias como devolvidas.
     */
    RESEND_WEBHOOK_SECRET: z.string().min(16).optional(),
    TRUST_PROXY: z.coerce.boolean().default(false),
  });

export const workerEnvSchema = base
  .merge(database)
  .merge(redis)
  .extend({
    WORKER_CONCURRENCY: z.coerce.number().int().positive().default(5),
  });

export type ApiEnv = z.infer<typeof apiEnvSchema>;
export type WorkerEnv = z.infer<typeof workerEnvSchema>;

/**
 * Lê e valida. Em erro, imprime *quais* variáveis faltam e encerra — nunca sobe pela metade.
 */
export function loadEnv<T extends z.ZodTypeAny>(
  schema: T,
  source: NodeJS.ProcessEnv = process.env,
): z.infer<T> {
  const parsed = schema.safeParse(source);
  if (!parsed.success) {
    const problemas = parsed.error.issues
      .map((i) => `  • ${i.path.join('.') || '(raiz)'}: ${i.message}`)
      .join('\n');
    throw new Error(`Variáveis de ambiente inválidas:\n${problemas}`);
  }
  return parsed.data;
}
